import { forwardRef, useCallback, useEffect, useId, useImperativeHandle, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import {
    BoardStore, INK, LINE_HEIGHT, MAX_STROKE_NUMBERS, NOTE_H, NOTE_W, bounds, clampZoom, contentBounds, drawObject,
    fitView, fontFor, hitTest, inkFor, intersects, moveObject, newObjectId, normRect, paintBoard, thinStroke, topHit,
    type Rect, type View, type WbObject, type WbOp
} from "./boardModel";

export type Tool = "select" | "hand" | "pen" | "highlighter" | "eraser" | "rect" | "ellipse" | "arrow" | "line" | "text" | "note";

export interface WhiteboardCanvasHandle {
    getView: () => View;
    /** Centre + zoom, so a follower with a different screen size sees the same middle. */
    getCenter: () => { cx: number; cy: number; zoom: number };
    setCenter: (cx: number, cy: number, zoom: number) => void;
    fit: () => void;
    zoomBy: (factor: number) => void;
    deleteSelection: () => void;
    clearSelection: () => void;
    focus: () => void;
}

interface Props {
    store: BoardStore;
    dark: boolean;
    /** Pan and zoom only (the Recap, or a locked board for non-presenters). */
    readOnly?: boolean;
    tool: Tool;
    color: string;
    size: number;
    /** A local change: the ops to apply and send, and the ops that undo it. */
    onCommit?: (ops: WbOp[], undo: WbOp[]) => void;
    /** What's being drawn right now (null when finished or cancelled). */
    onPreview?: (obj: WbObject | null) => void;
    /** `byUser` when the person panned or zoomed themselves (drops "follow"). */
    onViewChange?: (view: View, byUser: boolean) => void;
    onSelectionChange?: (count: number) => void;
    /** Placed a text box or sticky note: the parent may switch back to Select. */
    onToolDone?: () => void;
    className?: string;
    ariaLabel: string;
}

type Gesture =
    | { kind: "pan"; pointerId: number; sx: number; sy: number; view: View }
    | { kind: "draw"; pointerId: number }
    | { kind: "shape"; pointerId: number; sx: number; sy: number }
    | { kind: "erase"; pointerId: number }
    | { kind: "move"; pointerId: number; startX: number; startY: number; originals: WbObject[]; moved: boolean }
    | { kind: "marquee"; pointerId: number; startX: number; startY: number; base: Set<string> };

interface EditState {
    obj: WbObject;
    text: string;
    isNew: boolean;
    original?: WbObject;
}

const ERASER_PX = 10;
const HIT_PX = 6;
const DRAW_TOOLS: ReadonlySet<Tool> = new Set(["pen", "highlighter", "rect", "ellipse", "arrow", "line", "eraser", "text", "note"]);

/**
 * The whiteboard surface: a <canvas> with pan / zoom (wheel, pinch, space+drag,
 * the Pan tool), freehand ink with pen pressure-free smoothing, shapes, text,
 * sticky notes, an object eraser and select/move. It never talks to the
 * network — local changes go out through `onCommit` and the parent applies
 * them to the store — so the same component renders the read-only board in
 * the Recap. Pointer events cover mouse, touch and pen alike.
 */
export const WhiteboardCanvas = forwardRef<WhiteboardCanvasHandle, Props>(function WhiteboardCanvas(props, ref) {
    const { store, dark, readOnly = false, tool, className, ariaLabel } = props;
    const propsRef = useRef(props);
    propsRef.current = props;

    const containerRef = useRef<HTMLDivElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const editorRef = useRef<HTMLTextAreaElement>(null);
    const viewRef = useRef<View>({ x: 0, y: 0, zoom: 1 });
    const sizeRef = useRef({ w: 0, h: 0, dpr: 1 });
    const measuredRef = useRef(false);
    const selectionRef = useRef<Set<string>>(new Set());
    const draftRef = useRef<WbObject | null>(null);
    const overridesRef = useRef<Map<string, WbObject> | null>(null);
    const hiddenRef = useRef<Set<string>>(new Set());
    const marqueeRef = useRef<Rect | null>(null);
    const eraserAtRef = useRef<{ x: number; y: number } | null>(null);
    const gestureRef = useRef<Gesture | null>(null);
    const pointersRef = useRef(new Map<number, { x: number; y: number }>());
    const pinchRef = useRef<{ dist: number; cx: number; cy: number; view: View } | null>(null);
    const spaceRef = useRef(false);
    const frameRef = useRef(0);
    const [editing, setEditingState] = useState<EditState | null>(null);
    const editingRef = useRef<EditState | null>(null);
    const [, setViewTick] = useState(0);
    const helpId = useId();

    const setEditing = (e: EditState | null) => {
        editingRef.current = e;
        setEditingState(e);
    };

    // ── painting ─────────────────────────────────────────────────────────
    const paint = useCallback(() => {
        frameRef.current = 0;
        const canvas = canvasRef.current;
        const ctx = canvas?.getContext("2d");
        if (!canvas || !ctx) return;
        const { w, h, dpr } = sizeRef.current;
        const view = viewRef.current;
        const isDark = propsRef.current.dark;
        const s = propsRef.current.store;
        let objects = s.list();
        const overrides = overridesRef.current;
        const hidden = hiddenRef.current;
        if (overrides || hidden.size > 0) {
            objects = objects.filter((o) => !hidden.has(o.id)).map((o) => overrides?.get(o.id) ?? o);
        }
        paintBoard(ctx, objects, view, w, h, dpr, isDark);

        // Still in world space: other people's strokes in progress, then mine.
        for (const p of s.previews.values()) drawObject(ctx, p.obj, isDark, 0.55);
        if (draftRef.current) drawObject(ctx, draftRef.current, isDark);

        const sel = selectionRef.current;
        if (sel.size > 0) {
            ctx.save();
            ctx.strokeStyle = "#0078d4";
            ctx.lineWidth = 1.5 / view.zoom;
            ctx.setLineDash([6 / view.zoom, 4 / view.zoom]);
            const pad = 4 / view.zoom;
            for (const o of objects) {
                if (!sel.has(o.id)) continue;
                const b = bounds(o);
                ctx.strokeRect(b.x1 - pad, b.y1 - pad, b.x2 - b.x1 + pad * 2, b.y2 - b.y1 + pad * 2);
            }
            ctx.restore();
        }
        const m = marqueeRef.current;
        if (m) {
            ctx.save();
            ctx.fillStyle = "rgba(0,120,212,0.08)";
            ctx.strokeStyle = "#0078d4";
            ctx.lineWidth = 1 / view.zoom;
            ctx.fillRect(m.x1, m.y1, m.x2 - m.x1, m.y2 - m.y1);
            ctx.strokeRect(m.x1, m.y1, m.x2 - m.x1, m.y2 - m.y1);
            ctx.restore();
        }

        // Screen space: who is drawing what, and the eraser ring.
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.font = fontFor(11);
        ctx.textBaseline = "middle";
        for (const p of s.previews.values()) {
            if (!p.name) continue;
            const b = bounds(p.obj);
            const sx = (b.x2 - view.x) * view.zoom + 4;
            const sy = (b.y2 - view.y) * view.zoom + 4;
            const tw = ctx.measureText(p.name).width;
            ctx.fillStyle = "rgba(0,120,212,0.9)";
            ctx.fillRect(sx, sy, tw + 10, 18);
            ctx.fillStyle = "#fff";
            ctx.fillText(p.name, sx + 5, sy + 9);
        }
        const er = eraserAtRef.current;
        if (er && propsRef.current.tool === "eraser" && !propsRef.current.readOnly) {
            ctx.strokeStyle = isDark ? "#f4f4f5" : "#3f3f46";
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.arc(er.x, er.y, ERASER_PX, 0, Math.PI * 2);
            ctx.stroke();
        }
    }, []);

    const requestPaint = useCallback(() => {
        if (!frameRef.current) frameRef.current = requestAnimationFrame(paint);
    }, [paint]);

    const applyView = useCallback((v: View, byUser: boolean) => {
        viewRef.current = { x: v.x, y: v.y, zoom: clampZoom(v.zoom) };
        requestPaint();
        propsRef.current.onViewChange?.(viewRef.current, byUser);
        if (editingRef.current) setViewTick((t) => t + 1);
    }, [requestPaint]);

    const fit = useCallback(() => {
        const { w, h } = sizeRef.current;
        applyView(fitView(contentBounds(propsRef.current.store.list()), w, h), false);
    }, [applyView]);

    const zoomAt = useCallback((sx: number, sy: number, z: number, byUser = true) => {
        const v = viewRef.current;
        const zoom = clampZoom(z);
        const wx = v.x + sx / v.zoom;
        const wy = v.y + sy / v.zoom;
        applyView({ x: wx - sx / zoom, y: wy - sy / zoom, zoom }, byUser);
    }, [applyView]);

    const notifySelection = () => propsRef.current.onSelectionChange?.(selectionRef.current.size);

    const commit = (ops: WbOp[], undo: WbOp[]) => {
        if (ops.length > 0) propsRef.current.onCommit?.(ops, undo);
    };

    const deleteSelection = useCallback(() => {
        const s = propsRef.current.store;
        const originals = s.list().filter((o) => selectionRef.current.has(o.id));
        selectionRef.current = new Set();
        notifySelection();
        if (originals.length > 0) {
            commit([{ op: "delete", ids: originals.map((o) => o.id) }], originals.map((o) => ({ op: "add" as const, obj: o })));
        }
        requestPaint();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requestPaint]);

    useImperativeHandle(ref, () => ({
        getView: () => viewRef.current,
        getCenter: () => {
            const { w, h } = sizeRef.current;
            const v = viewRef.current;
            return { cx: v.x + w / 2 / v.zoom, cy: v.y + h / 2 / v.zoom, zoom: v.zoom };
        },
        setCenter: (cx, cy, zoom) => {
            const { w, h } = sizeRef.current;
            const z = clampZoom(zoom);
            applyView({ x: cx - w / 2 / z, y: cy - h / 2 / z, zoom: z }, false);
        },
        fit,
        zoomBy: (factor) => {
            const { w, h } = sizeRef.current;
            zoomAt(w / 2, h / 2, viewRef.current.zoom * factor);
        },
        deleteSelection,
        clearSelection: () => {
            selectionRef.current = new Set();
            notifySelection();
            requestPaint();
        },
        focus: () => containerRef.current?.focus({ preventScroll: true })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [applyView, fit, zoomAt, deleteSelection, requestPaint]);

    // ── size, store, theme ──────────────────────────────────────────────
    useEffect(() => {
        const el = containerRef.current;
        const canvas = canvasRef.current;
        if (!el || !canvas) return;
        const measure = () => {
            const r = el.getBoundingClientRect();
            const dpr = Math.min(window.devicePixelRatio || 1, 2.5);
            const w = Math.max(1, Math.round(r.width));
            const h = Math.max(1, Math.round(r.height));
            sizeRef.current = { w, h, dpr };
            canvas.width = Math.round(w * dpr);
            canvas.height = Math.round(h * dpr);
            if (!measuredRef.current) {
                measuredRef.current = true;
                viewRef.current = fitView(contentBounds(propsRef.current.store.list()), w, h);
                propsRef.current.onViewChange?.(viewRef.current, false);
            }
            paint();
        };
        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(el);
        return () => ro.disconnect();
    }, [paint]);

    useEffect(() => store.subscribe(() => {
        // Anything selected that someone else erased is no longer selected.
        const sel = selectionRef.current;
        if (sel.size > 0) {
            let changed = false;
            for (const id of Array.from(sel)) {
                if (!store.get(id)) {
                    sel.delete(id);
                    changed = true;
                }
            }
            if (changed) notifySelection();
        }
        requestPaint();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [store, requestPaint]);

    useEffect(() => { requestPaint(); }, [dark, tool, readOnly, requestPaint]);

    // Switching tools drops a half-made selection / eraser ring.
    useEffect(() => {
        if (tool !== "select" && selectionRef.current.size > 0) {
            selectionRef.current = new Set();
            notifySelection();
        }
        if (tool !== "eraser") eraserAtRef.current = null;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tool]);

    useEffect(() => () => { if (frameRef.current) cancelAnimationFrame(frameRef.current); }, []);

    // Wheel: pan, or zoom with Ctrl/⌘ (and trackpad pinch). Non-passive so the
    // page doesn't scroll; a read-only board lets plain scrolling through.
    useEffect(() => {
        const el = canvasRef.current;
        if (!el) return;
        const onWheel = (e: WheelEvent) => {
            const r = el.getBoundingClientRect();
            const sx = e.clientX - r.left;
            const sy = e.clientY - r.top;
            if (e.ctrlKey || e.metaKey) {
                e.preventDefault();
                zoomAt(sx, sy, viewRef.current.zoom * Math.exp(-e.deltaY * 0.0015));
                return;
            }
            if (propsRef.current.readOnly) return;
            e.preventDefault();
            const v = viewRef.current;
            applyView({ x: v.x + e.deltaX / v.zoom, y: v.y + e.deltaY / v.zoom, zoom: v.zoom }, true);
        };
        el.addEventListener("wheel", onWheel, { passive: false });
        return () => el.removeEventListener("wheel", onWheel);
    }, [applyView, zoomAt]);

    // ── pointer input ───────────────────────────────────────────────────
    const screenPoint = (e: { clientX: number; clientY: number }) => {
        const r = canvasRef.current!.getBoundingClientRect();
        return { sx: e.clientX - r.left, sy: e.clientY - r.top };
    };
    const toWorld = (sx: number, sy: number) => {
        const v = viewRef.current;
        return { x: v.x + sx / v.zoom, y: v.y + sy / v.zoom };
    };

    const cancelGesture = () => {
        if (draftRef.current) propsRef.current.onPreview?.(null);
        draftRef.current = null;
        overridesRef.current = null;
        marqueeRef.current = null;
        if (gestureRef.current?.kind === "erase") hiddenRef.current = new Set();
        gestureRef.current = null;
        requestPaint();
    };

    const eraseAt = (x: number, y: number) => {
        const tol = ERASER_PX / viewRef.current.zoom;
        let changed = false;
        for (const o of propsRef.current.store.list()) {
            if (hiddenRef.current.has(o.id)) continue;
            if (hitTest(o, x, y, tol)) {
                hiddenRef.current.add(o.id);
                changed = true;
            }
        }
        if (changed) requestPaint();
    };

    const startEdit = (obj: WbObject, isNew: boolean) => {
        if (!isNew) hiddenRef.current.add(obj.id);
        setEditing({ obj, text: obj.text ?? "", isNew, original: isNew ? undefined : obj });
        requestPaint();
    };

    const finishEdit = (save: boolean) => {
        const ed = editingRef.current;
        if (!ed) return;
        setEditing(null);
        hiddenRef.current.delete(ed.obj.id);
        const text = ed.text.replace(/\r/g, "").slice(0, 2000);
        if (save) {
            if (ed.isNew) {
                if (ed.obj.kind === "note" || text.trim()) {
                    const obj = { ...ed.obj, text };
                    commit([{ op: "add", obj }], [{ op: "delete", ids: [obj.id] }]);
                }
            } else if (ed.original && text !== (ed.original.text ?? "")) {
                const orig = ed.original;
                if (orig.kind === "text" && !text.trim()) {
                    commit([{ op: "delete", ids: [orig.id] }], [{ op: "add", obj: orig }]);
                } else {
                    commit([{ op: "update", obj: { ...orig, text } }], [{ op: "update", obj: orig }]);
                }
            }
            propsRef.current.onToolDone?.();
        }
        requestPaint();
        containerRef.current?.focus({ preventScroll: true });
    };

    const onPointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
        if (editingRef.current) {
            finishEdit(true);
            return;
        }
        containerRef.current?.focus({ preventScroll: true });
        const { sx, sy } = screenPoint(e);
        pointersRef.current.set(e.pointerId, { x: sx, y: sy });
        try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* not capturable */ }

        // Two fingers: pinch-zoom / pan, whatever tool is active.
        if (pointersRef.current.size === 2) {
            cancelGesture();
            const [a, b] = Array.from(pointersRef.current.values()) as [{ x: number; y: number }, { x: number; y: number }];
            pinchRef.current = {
                dist: Math.max(1, Math.hypot(a.x - b.x, a.y - b.y)),
                cx: (a.x + b.x) / 2,
                cy: (a.y + b.y) / 2,
                view: { ...viewRef.current }
            };
            return;
        }
        if (pointersRef.current.size > 2) return;

        const p = propsRef.current;
        const activeTool: Tool = p.readOnly ? "hand" : p.tool;
        if (e.button === 1 || activeTool === "hand" || spaceRef.current) {
            gestureRef.current = { kind: "pan", pointerId: e.pointerId, sx, sy, view: { ...viewRef.current } };
            return;
        }
        if (e.pointerType === "mouse" && e.button !== 0) return;

        const w = toWorld(sx, sy);
        const tol = HIT_PX / viewRef.current.zoom;
        const list = p.store.list();
        switch (activeTool) {
            case "pen":
            case "highlighter":
                draftRef.current = { id: newObjectId(), kind: activeTool, color: p.color, size: p.size, points: [w.x, w.y] };
                gestureRef.current = { kind: "draw", pointerId: e.pointerId };
                p.onPreview?.(draftRef.current);
                break;
            case "rect":
            case "ellipse":
            case "arrow":
            case "line":
                draftRef.current = { id: newObjectId(), kind: activeTool, color: p.color, size: p.size, points: [w.x, w.y, w.x, w.y] };
                gestureRef.current = { kind: "shape", pointerId: e.pointerId, sx, sy };
                break;
            case "eraser":
                gestureRef.current = { kind: "erase", pointerId: e.pointerId };
                hiddenRef.current = new Set();
                eraseAt(w.x, w.y);
                break;
            case "text": {
                const hit = topHit(list, w.x, w.y, tol);
                if (hit?.kind === "text") startEdit(hit, false);
                else startEdit({ id: newObjectId(), kind: "text", color: p.color, size: p.size, x: w.x, y: w.y - p.size * 0.6, text: "" }, true);
                break;
            }
            case "note": {
                const hit = topHit(list, w.x, w.y, tol);
                if (hit?.kind === "note") startEdit(hit, false);
                else startEdit({ id: newObjectId(), kind: "note", color: p.color, size: 16, x: w.x - NOTE_W / 2, y: w.y - NOTE_H / 2, w: NOTE_W, h: NOTE_H, text: "" }, true);
                break;
            }
            case "select": {
                const hit = topHit(list, w.x, w.y, tol);
                const sel = selectionRef.current;
                if (hit) {
                    if (e.shiftKey) {
                        if (sel.has(hit.id)) sel.delete(hit.id);
                        else sel.add(hit.id);
                    } else if (!sel.has(hit.id)) {
                        selectionRef.current = new Set([hit.id]);
                    }
                    const chosen = selectionRef.current;
                    gestureRef.current = {
                        kind: "move", pointerId: e.pointerId, startX: w.x, startY: w.y, moved: false,
                        originals: list.filter((o) => chosen.has(o.id))
                    };
                } else {
                    const base = e.shiftKey ? new Set(sel) : new Set<string>();
                    selectionRef.current = new Set(base);
                    gestureRef.current = { kind: "marquee", pointerId: e.pointerId, startX: w.x, startY: w.y, base };
                    marqueeRef.current = normRect(w.x, w.y, w.x, w.y);
                }
                notifySelection();
                break;
            }
            default:
                break;
        }
        requestPaint();
    };

    const onPointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
        const { sx, sy } = screenPoint(e);
        if (pointersRef.current.has(e.pointerId)) pointersRef.current.set(e.pointerId, { x: sx, y: sy });

        const pinch = pinchRef.current;
        if (pinch && pointersRef.current.size >= 2) {
            const [a, b] = Array.from(pointersRef.current.values()) as [{ x: number; y: number }, { x: number; y: number }];
            const dist = Math.max(1, Math.hypot(a.x - b.x, a.y - b.y));
            const cx = (a.x + b.x) / 2;
            const cy = (a.y + b.y) / 2;
            const zoom = clampZoom(pinch.view.zoom * (dist / pinch.dist));
            const wx = pinch.view.x + pinch.cx / pinch.view.zoom;
            const wy = pinch.view.y + pinch.cy / pinch.view.zoom;
            applyView({ x: wx - cx / zoom, y: wy - cy / zoom, zoom }, true);
            return;
        }

        if (propsRef.current.tool === "eraser" && !propsRef.current.readOnly) {
            eraserAtRef.current = { x: sx, y: sy };
            requestPaint();
        }
        const g = gestureRef.current;
        if (!g || g.pointerId !== e.pointerId) return;
        const w = toWorld(sx, sy);
        switch (g.kind) {
            case "pan":
                applyView({ x: g.view.x - (sx - g.sx) / g.view.zoom, y: g.view.y - (sy - g.sy) / g.view.zoom, zoom: g.view.zoom }, true);
                break;
            case "draw": {
                const d = draftRef.current;
                if (!d?.points) break;
                // Coalesced events keep fast pen strokes smooth.
                const native = e.nativeEvent as PointerEvent & { getCoalescedEvents?: () => PointerEvent[] };
                const samples = native.getCoalescedEvents?.() ?? [];
                const points = samples.length > 0
                    ? samples.map((c) => {
                        const s = screenPoint(c);
                        return toWorld(s.sx, s.sy);
                    })
                    : [w];
                for (const pt of points) {
                    const lx = d.points[d.points.length - 2]!;
                    const ly = d.points[d.points.length - 1]!;
                    if (Math.hypot(pt.x - lx, pt.y - ly) * viewRef.current.zoom >= 1.5) d.points.push(pt.x, pt.y);
                }
                propsRef.current.onPreview?.(d);
                requestPaint();
                break;
            }
            case "shape": {
                const d = draftRef.current;
                if (!d?.points) break;
                let x2 = w.x;
                let y2 = w.y;
                const x1 = d.points[0]!;
                const y1 = d.points[1]!;
                if (e.shiftKey) {
                    if (d.kind === "rect" || d.kind === "ellipse") {
                        // Square / circle.
                        const side = Math.max(Math.abs(x2 - x1), Math.abs(y2 - y1));
                        x2 = x1 + Math.sign(x2 - x1 || 1) * side;
                        y2 = y1 + Math.sign(y2 - y1 || 1) * side;
                    } else {
                        // Snap to 45°.
                        const angle = Math.round(Math.atan2(y2 - y1, x2 - x1) / (Math.PI / 4)) * (Math.PI / 4);
                        const len = Math.hypot(x2 - x1, y2 - y1);
                        x2 = x1 + Math.cos(angle) * len;
                        y2 = y1 + Math.sin(angle) * len;
                    }
                }
                d.points = [x1, y1, x2, y2];
                propsRef.current.onPreview?.(d);
                requestPaint();
                break;
            }
            case "erase":
                eraseAt(w.x, w.y);
                break;
            case "move": {
                const dx = w.x - g.startX;
                const dy = w.y - g.startY;
                if (dx !== 0 || dy !== 0) g.moved = true;
                overridesRef.current = new Map(g.originals.map((o) => [o.id, moveObject(o, dx, dy)]));
                requestPaint();
                break;
            }
            case "marquee": {
                const r = normRect(g.startX, g.startY, w.x, w.y);
                marqueeRef.current = r;
                const next = new Set(g.base);
                for (const o of propsRef.current.store.list()) if (intersects(bounds(o), r)) next.add(o.id);
                selectionRef.current = next;
                notifySelection();
                requestPaint();
                break;
            }
        }
    };

    const onPointerUp = (e: React.PointerEvent<HTMLCanvasElement>) => {
        pointersRef.current.delete(e.pointerId);
        if (pinchRef.current) {
            if (pointersRef.current.size < 2) pinchRef.current = null;
            return;
        }
        const g = gestureRef.current;
        if (!g || g.pointerId !== e.pointerId) return;
        gestureRef.current = null;
        if (e.type === "pointercancel") {
            gestureRef.current = g;
            cancelGesture();
            return;
        }
        const p = propsRef.current;
        switch (g.kind) {
            case "draw": {
                const d = draftRef.current;
                draftRef.current = null;
                p.onPreview?.(null);
                if (!d?.points) break;
                const obj = { ...d, points: thinStroke(d.points, MAX_STROKE_NUMBERS) };
                commit([{ op: "add", obj }], [{ op: "delete", ids: [obj.id] }]);
                break;
            }
            case "shape": {
                const d = draftRef.current;
                draftRef.current = null;
                p.onPreview?.(null);
                const { sx, sy } = screenPoint(e);
                if (!d || Math.hypot(sx - g.sx, sy - g.sy) < 3) break;
                commit([{ op: "add", obj: d }], [{ op: "delete", ids: [d.id] }]);
                break;
            }
            case "erase": {
                const ids = hiddenRef.current;
                hiddenRef.current = new Set();
                if (ids.size === 0) break;
                const originals = p.store.list().filter((o) => ids.has(o.id));
                commit([{ op: "delete", ids: originals.map((o) => o.id) }], originals.map((o) => ({ op: "add" as const, obj: o })));
                break;
            }
            case "move": {
                const overrides = overridesRef.current;
                overridesRef.current = null;
                if (!g.moved || !overrides) break;
                const moved = g.originals.map((o) => overrides.get(o.id)).filter((o): o is WbObject => Boolean(o));
                commit(moved.map((o) => ({ op: "update" as const, obj: o })), g.originals.map((o) => ({ op: "update" as const, obj: o })));
                break;
            }
            case "marquee":
                marqueeRef.current = null;
                break;
            default:
                break;
        }
        requestPaint();
    };

    const onDoubleClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
        if (readOnly || propsRef.current.tool !== "select") return;
        const { sx, sy } = screenPoint(e);
        const w = toWorld(sx, sy);
        const hit = topHit(propsRef.current.store.list(), w.x, w.y, HIT_PX / viewRef.current.zoom);
        if (hit && (hit.kind === "text" || hit.kind === "note")) {
            selectionRef.current = new Set();
            notifySelection();
            startEdit(hit, false);
        }
    };

    // ── keyboard ────────────────────────────────────────────────────────
    const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.target !== containerRef.current) return;
        const v = viewRef.current;
        const { w, h } = sizeRef.current;
        if (e.key === " ") {
            spaceRef.current = true;
            e.preventDefault();
            return;
        }
        if (e.key === "+" || e.key === "=") {
            e.preventDefault();
            zoomAt(w / 2, h / 2, v.zoom * 1.2);
            return;
        }
        if (e.key === "-" || e.key === "_") {
            e.preventDefault();
            zoomAt(w / 2, h / 2, v.zoom / 1.2);
            return;
        }
        if (e.key === "0") {
            e.preventDefault();
            fit();
            return;
        }
        const arrows: Record<string, [number, number]> = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] };
        const dir = arrows[e.key];
        if (dir) {
            e.preventDefault();
            const sel = selectionRef.current;
            if (!readOnly && sel.size > 0) {
                // Nudge the selection.
                const step = (e.shiftKey ? 20 : 4) / v.zoom;
                const originals = propsRef.current.store.list().filter((o) => sel.has(o.id));
                commit(
                    originals.map((o) => ({ op: "update" as const, obj: moveObject(o, dir[0] * step, dir[1] * step) })),
                    originals.map((o) => ({ op: "update" as const, obj: o })));
            } else {
                const step = (e.shiftKey ? 240 : 60) / v.zoom;
                applyView({ x: v.x + dir[0] * step, y: v.y + dir[1] * step, zoom: v.zoom }, true);
            }
            return;
        }
        if (readOnly) return;
        if (e.key === "Delete" || e.key === "Backspace") {
            if (selectionRef.current.size > 0) {
                e.preventDefault();
                deleteSelection();
            }
            return;
        }
        if (e.key === "Escape") {
            if (gestureRef.current) cancelGesture();
            else if (selectionRef.current.size > 0) {
                selectionRef.current = new Set();
                notifySelection();
                requestPaint();
            }
            return;
        }
        if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "a" && propsRef.current.tool === "select") {
            e.preventDefault();
            selectionRef.current = new Set(propsRef.current.store.list().map((o) => o.id));
            notifySelection();
            requestPaint();
        }
    };
    const onKeyUp = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === " ") spaceRef.current = false;
    };

    // ── text / sticky-note editor overlay ───────────────────────────────
    const editor = editing ? (() => {
        const v = viewRef.current;
        const o = editing.obj;
        const isNote = o.kind === "note";
        const left = ((o.x ?? 0) - v.x) * v.zoom;
        const top = ((o.y ?? 0) - v.y) * v.zoom;
        const lines = Math.max(1, editing.text.split("\n").length);
        return (
            <textarea
                ref={editorRef}
                autoFocus
                aria-label={isNote ? "Sticky note text" : "Text"}
                value={editing.text}
                maxLength={2000}
                spellCheck
                onChange={(e) => setEditing({ ...editing, text: e.target.value })}
                onBlur={() => finishEdit(true)}
                onKeyDown={(e) => {
                    e.stopPropagation();
                    if (e.key === "Escape") {
                        e.preventDefault();
                        finishEdit(false);
                    } else if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                        e.preventDefault();
                        finishEdit(true);
                    }
                }}
                placeholder={isNote ? "Type a note…" : "Type…"}
                className="absolute z-10 resize-none overflow-hidden rounded-sm border-2 border-primary bg-transparent outline-none placeholder:opacity-50"
                style={{
                    left,
                    top,
                    width: isNote ? (o.w ?? NOTE_W) * v.zoom : Math.max(180, 360 * v.zoom),
                    height: isNote ? (o.h ?? NOTE_H) * v.zoom : lines * o.size * LINE_HEIGHT * v.zoom + 8,
                    fontSize: o.size * v.zoom,
                    lineHeight: LINE_HEIGHT,
                    padding: isNote ? 10 * v.zoom : 0,
                    background: isNote ? o.color : "transparent",
                    color: isNote ? INK : inkFor(o.color, dark),
                    fontFamily: "ui-sans-serif, system-ui, -apple-system, Roboto, sans-serif"
                }}
            />
        );
    })() : null;

    const cursor = readOnly ? "grab"
        : tool === "hand" ? "grab"
            : tool === "select" ? "default"
                : tool === "text" ? "text"
                    : tool === "eraser" ? "none"
                        : DRAW_TOOLS.has(tool) ? "crosshair" : "default";

    return (
        <div
            ref={containerRef}
            tabIndex={0}
            role="application"
            aria-roledescription="whiteboard"
            aria-label={ariaLabel}
            aria-describedby={helpId}
            onKeyDown={onKeyDown}
            onKeyUp={onKeyUp}
            className={cn("relative overflow-hidden outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary", className)}
        >
            <span id={helpId} className="sr-only">
                {readOnly
                    ? "Drag or use the arrow keys to pan; plus and minus zoom; 0 fits the board."
                    : "Pick a tool from the toolbar, then draw with a mouse, pen or finger. Arrow keys pan (or move the selection), plus and minus zoom, 0 fits the board, Delete removes the selection, Escape cancels."}
            </span>
            <canvas
                ref={canvasRef}
                className="block h-full w-full touch-none select-none"
                style={{ cursor }}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerUp}
                onPointerLeave={() => {
                    if (eraserAtRef.current) {
                        eraserAtRef.current = null;
                        requestPaint();
                    }
                }}
                onDoubleClick={onDoubleClick}
                onContextMenu={(e) => e.preventDefault()}
            />
            {editor}
        </div>
    );
});
